import {yupResolver} from '@hookform/resolvers/yup';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {Alert, ScrollView, Text, View} from 'react-native';
import {Card, Divider} from 'react-native-paper';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import BTButton from '../Components/BTButton';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {
  useDeletePOEChangeTableMutation,
  useInsertPOEChangeTableMutation,
  useUpdatePOEChangeTableMutation,
} from '../redux/services/poechangetableApi';
import {useGetPOEOrdersQuery} from '../redux/services/poeorderApi';
import {useGetPOMTablesQuery} from '../redux/services/pomtableApi';
import {useGetEmployeesQuery} from '../redux/services/emmemployeeApi';
import {RootState} from '../redux/store';
import styles from '../styles/Styles';
import {ShowToast, retrieveSetting} from '../Common/common';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const validationSchema = yup.object({
  ct_from: yup.number().moreThan(0, 'From Table is required'),
  ct_kot: yup.number().moreThan(0, 'KOT Number is required'),
  ct_to: yup.number().moreThan(0, 'To Table is required'),
  ct_employee: yup.number().moreThan(0, 'Employee is required'),
  ct_narr: yup.string().required('Remarks are required'),
});

const POSChangeTable = ({navigation, route}: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const glogic = useSelector((state: RootState) => state.glogic.glogic);
  const cid = gdata.company;

  // Get default outlet from settings or glogic
  const [defOutlet, setDefOutlet] = useState(0);

  useEffect(() => {
    const retDefOutlet = async () => {
      const outlet = await retrieveSetting('defoutlet');
      setDefOutlet(Number(outlet) || 13);
    };
    retDefOutlet();
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const [selectedTable, setSelectedTable] = useState(0);
  const [selectedKot, setSelectedKot] = useState(0);
  const [defaultEmployee, setDefaultEmployee] = useState<number | null>(null);

  const [insertPOEChangeTable] = useInsertPOEChangeTableMutation();
  const [updatePOEChangeTable] = useUpdatePOEChangeTableMutation();
  const [deletePOEChangeTable] = useDeletePOEChangeTableMutation();

  // Get outlet ID (from glogic or settings)
  const olid = glogic?.outletid || defOutlet || 13;

  // Get all tables for the outlet
  const {data: tablesData} = useGetPOMTablesQuery(
    {cid: cid, olid: olid},
    {skip: olid === 0},
  );

  // Get all employees for the company
  const {data: employeeData} = useGetEmployeesQuery(cid);

  // Get all orders
  const {data: orderData} = useGetPOEOrdersQuery(cid);

  // Set default employee when employee data is loaded
  useEffect(() => {
    if (employeeData && employeeData.length > 0 && !defaultEmployee) {
      const firstActiveEmployee = employeeData.find(
        emp => emp.em_flag === '1' || emp.em_flag === '1',
      );
      if (firstActiveEmployee) {
        setDefaultEmployee(Number(firstActiveEmployee.em_id));
      }
    }
  }, [employeeData, defaultEmployee]);

  // Prepare tables for "From Table" (show all tables)
  const fromTableOptions =
    tablesData
      ?.filter(table => table.tb_status > 0)
      ?.map(table => ({
        label: `${table.tb_name}${
          table.tb_areaname ? ' - ' + table.tb_areaname : ''
        }`,
        id: table.tb_id,
        value: table,
      })) || [];
  // All tables for "To Table" selection
  const toTableOptions =
    tablesData?.map(table => ({
      label: `${table.tb_name}${
        table.tb_areaname ? ' - ' + table.tb_areaname : ''
      }`,
      id: table.tb_id,
      value: table,
    })) || [];

  // Prepare KOT options for dropdown (only show KOTs from selected table)
  const kotOptions =
    orderData
      ?.filter(kot => kot.or_table === selectedTable && kot.or_flag === 0)
      .map(kot => ({
        label: `KOT #${kot.or_no} - ${kot.or_name} (${kot.or_pax || 0} pax)`,
        id: kot.or_id,
        value: kot,
      })) || [];

  // Prepare employee options for dropdown (only active employees)
  const employeeOptions =
    employeeData
      ?.filter(employee => employee.em_flag === '1') // Only active employees
      ?.map(employee => ({
        label: `${employee.em_name} (${employee.em_code})`,
        id: employee.em_id,
        value: employee,
      })) || [];

  const methods = useForm<any>({
    defaultValues: {
      ct_id: 0,
      ct_company: cid,
      ct_date:
        moment(gdata.today).format('YYYY/MM/DD') + moment().format(' HH:mm:ss'),
      ct_outlet: olid,
      ct_from: 0,
      ct_kot: 0,
      ct_to: 0,
      ct_employee: defaultEmployee || 0,
      ct_narr: '',
      ct_flag: '1',
      ct_uid: gdata.user,
      ct_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
      ct_muid: gdata.user,
      ct_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    },
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<any> = async values => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const changeTableData = {
        ...values,
        ct_from: selectedTable,
        ct_to: values.ct_to,
      };

      if (parseInt(values.ct_id) !== 0) {
        await updatePOEChangeTable(changeTableData).unwrap();
        ShowToast('Data Updated Successfully');
      } else {
        await insertPOEChangeTable(changeTableData).unwrap();
        ShowToast('Data Saved Successfully');
      }

      methods.reset();
      navigation.goBack();
    } catch (error: any) {
      ShowToast(error?.data?.message || error?.message || 'Failed to save data', 'error');
    }
    setIsSaving(false);
  };

  const onDelete = async () => {
    Alert.alert(tp("BlueHMS"), tp("Do you want to Cancel Change Table?"), [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: 'OK',
        onPress: async () => {
          if (isSaving) return;
          setIsSaving(true);
          try {
            const values = methods.getValues();
            await deletePOEChangeTable(values).unwrap();
            ShowToast('Data Deleted Successfully');
            methods.reset();
            navigation.goBack();
          } catch (error: any) {
            ShowToast(error?.data?.message || error?.message || 'Failed to delete data', 'error');
          }
          setIsSaving(false);
        },
      },
    ]);
  };

  // Set default employee in form when available
  useEffect(() => {
    if (defaultEmployee) {
      methods.setValue('ct_employee', defaultEmployee);
    }
  }, [defaultEmployee]);

  useEffect(() => {
    if (route.params?.table) {
      const tableId = Number(route.params.table.tb_id);
      setSelectedTable(tableId);
      methods.setValue('ct_from', tableId);
      methods.setValue('ct_to', tableId);
    }
  }, [route.params?.table]);

  // Reset KOT selection when table changes
  useEffect(() => {
    if (selectedTable > 0) {
      methods.setValue('ct_kot', 0);
      setSelectedKot(0);
    }
  }, [selectedTable]);

  // Handle KOT selection change
  const handleKotChange = (kotId: string) => {
    setSelectedKot(Number(kotId));
    methods.setValue('ct_kot', Number(kotId));
  };

  // Handle table selection change
  const handleTableChange = (tableId: string) => {
    setSelectedTable(Number(tableId));
    methods.setValue('ct_from', Number(tableId));
    setValue('ct_from', Number(tableId));
  };

  const setValue = methods.setValue;

  return (
    <ScrollView
      contentContainerStyle={[
        styles.container,
        {
          flexGrow: 1,
          paddingBottom: 80,
        },
      ]}
      keyboardShouldPersistTaps="handled"
      style={{flex: 1}}>
      <View style={styles.header}>
        <Text style={styles.title}><T>Change Table</T></Text>
      </View>
      <FormProvider {...methods}>
        <Card style={[styles.card, styles.responsiveCard]}>
          <Card.Content style={{width: '100%', alignSelf: 'stretch'}}>
            <Row>
              <Col>
                <BTComboBox
                  name="ct_from"
                  label="From Table (Tables with KOTs)"
                  data={fromTableOptions}
                  placeholder={tp("Select From Table")}
                  value={selectedTable}
                  onValueChange={handleTableChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="ct_kot"
                  label="KOT Number"
                  data={kotOptions}
                  placeholder={tp("Select KOT")}
                  value={selectedKot}
                  onValueChange={handleKotChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="ct_to"
                  label="To Table"
                  data={toTableOptions}
                  placeholder={tp("Select To Table")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="ct_employee"
                  label="Employee"
                  data={employeeOptions}
                  placeholder={tp("Select Employee")}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="ct_narr"
                  label="Remarks"
                  placeholder={tp("Enter remarks for table change")}
                />
              </Col>
            </Row>
            <Divider />
            <Row>
              <Col>
                <BTButton
                  title="Save"
                  color="#146C94"
                  onPress={methods.handleSubmit(onSubmit)}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </Col>
              <Col>
                <BTButton
                  title="Cancel"
                  color="#146C94"
                  onPress={() => navigation.goBack()}
                  onLongPress={() => {}}
                />
              </Col>
              <Col>
                <BTButton
                  title="Delete"
                  color="#dc3545"
                  onPress={onDelete}
                  onLongPress={() => {}}
                  disabled={isSaving}
                />
              </Col>
            </Row>
          </Card.Content>
        </Card>
      </FormProvider>
    </ScrollView>
  );
};

export default POSChangeTable;

